import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

interface SortableEvent {
  oldIndex: number;
  newIndex: number;
  item: HTMLElement;
  to: HTMLElement;
  from: HTMLElement;
}

export default class extends Controller {
  static targets = ["sectionList", "questionList"];
  static values = {
    url: String,
    sectionUrl: String,
    questionUrl: String,
  };

  declare readonly sectionListTarget: HTMLElement;
  declare readonly questionListTargets: HTMLElement[];
  declare readonly urlValue: string;
  declare readonly sectionUrlValue: string;
  declare readonly questionUrlValue: string;
  declare sectionSortable: Sortable;
  declare questionSortables: Map<string, Sortable>;
  declare isDragging: boolean;

  initialize() {
    this.questionSortables = new Map();
    this.isDragging = false;
  }

  connect() {
    // Clean up any existing sortables first
    this.cleanupSortables();

    // Initialize new sortables
    requestAnimationFrame(() => {
      this.initializeSortables();
    });
  }

  disconnect() {
    this.cleanupSortables();
  }

  cleanupSortables() {
    if (this.isDragging) return; // Don't cleanup while dragging

    if (this.sectionSortable) {
      try {
        this.sectionSortable.destroy();
      } catch (e) {
        console.error("Error destroying section sortable:", e);
      }
      this.sectionSortable = null;
    }
    if (this.questionSortables) {
      this.questionSortables.forEach((sortable) => {
        try {
          sortable.destroy();
        } catch (e) {
          console.error("Error destroying sortable:", e);
        }
      });
      this.questionSortables.clear();
    }
  }

  questionListTargetConnected(element: HTMLElement) {
    if (this.isDragging) return; // Don't initialize new sortables while dragging

    requestAnimationFrame(() => {
      this.initializeQuestionSortable(element);
    });
  }

  initializeSortables() {
    this.initializeSectionSortable();
    this.initializeAllQuestionSortables();
  }

  initializeAllQuestionSortables() {
    if (this.isDragging) return; // Don't initialize while dragging

    this.questionListTargets.forEach((target) => {
      this.initializeQuestionSortable(target);
    });
  }

  initializeSectionSortable() {
    if (this.sectionSortable) {
      this.sectionSortable.destroy();
    }

    this.sectionSortable = new Sortable(this.sectionListTarget, {
      animation: 150,
      handle: ".section-handle",
      ghostClass: "opacity-30",
      chosenClass: "shadow-lg",
      dragClass: "shadow-xl",
      forceFallback: true,
      fallbackOnBody: false,
      direction: "vertical",
      onStart: (evt) => {
        this.isDragging = true;
        const sections = Array.from(
          this.sectionListTarget.children,
        ) as HTMLElement[];
        sections.forEach((section) => {
          const header = section.querySelector("[data-section-header]");
          Array.from(section.children).forEach((child) => {
            if (child !== header) {
              (child as HTMLElement).style.display = "none";
            }
          });
          if (header) {
            // Collapse the section to just its header. The section root has
            // vertical padding (p-4), so the header's own height is short of
            // the box we want — without adding the padding back the clamped
            // box cuts through the header text and the shadow lands mid-title.
            const styles = getComputedStyle(section);
            const verticalPadding =
              parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
            section.style.height = `${
              (header as HTMLElement).offsetHeight + verticalPadding
            }px`;
            section.style.overflow = "hidden";
            section.style.transition = "height 150ms ease-in-out";
          }
        });
      },
      onEnd: (evt) => {
        const sections = Array.from(
          this.sectionListTarget.children,
        ) as HTMLElement[];
        sections.forEach((section) => {
          Array.from(section.children).forEach((child) => {
            (child as HTMLElement).style.display = "";
          });
          section.style.height = "";
          section.style.overflow = "";
          section.style.transition = "";
        });
        if (evt.oldIndex !== evt.newIndex) {
          evt.item.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }

        this.updateSectionOrder(evt);
        this.isDragging = false;
      },
    });
  }

  initializeQuestionSortable(element: HTMLElement) {
    if (this.isDragging) return; // Don't initialize while dragging

    const existingSortable = this.questionSortables.get(element.id);
    if (existingSortable) {
      existingSortable.destroy();
      this.questionSortables.delete(element.id);
    }

    const sortable = new Sortable(element, {
      animation: 150,
      handle: ".question-handle",
      group: "questions",
      draggable: "[data-question-id]",
      ghostClass: "opacity-30",
      chosenClass: "shadow-lg",
      dragClass: "shadow-xl",
      forceFallback: true,
      fallbackOnBody: false,
      direction: "vertical",
      onStart: () => {
        this.isDragging = true;
      },
      onEnd: (evt) => {
        this.updateQuestionOrder(evt);
        this.isDragging = false;
      },
    });

    this.questionSortables.set(element.id, sortable);
  }

  updateSectionOrder(event: SortableEvent) {
    const sections = Array.from(this.sectionListTarget.children);
    const positions = sections.map((section, index) => ({
      id: section.getAttribute("data-section-id"),
      position: index,
    }));

    this.persistOrder(this.sectionUrlValue, { positions });
  }

  updateQuestionOrder(event: SortableEvent) {
    const sectionId = event.to.getAttribute("data-section-id");
    const questions = Array.from(event.to.children);
    const positions = questions.map((question, index) => ({
      id: question.getAttribute("data-question-id"),
      position: index,
      section_id: sectionId,
    }));

    const fromSectionId = event.from.getAttribute("data-section-id");
    if (fromSectionId !== sectionId) {
      // If moving between sections, update positions in both sections
      const fromQuestions = Array.from(event.from.children);
      const fromPositions = fromQuestions.map((question, index) => ({
        id: question.getAttribute("data-question-id"),
        position: index,
        section_id: fromSectionId,
      }));
      positions.push(...fromPositions);
    }

    this.persistOrder(this.questionUrlValue, { positions });
  }

  // Drag-sort saves go through a fire-and-forget PATCH; the builder header's
  // save indicator (data-controller="save-indicator") listens on `window` for
  // `survey-save` events to flash a "Saving… / Saved" badge — the same
  // contract used by form_auto_save_controller. Broadcast it here so a
  // reorder gets the same visible confirmation as an inline field edit.
  private async persistOrder(url: string, body: object) {
    this.broadcastSave("saving");
    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document
              .querySelector("[name='csrf-token']")
              ?.getAttribute("content") || "",
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.broadcastSave("saved");
    } catch (error) {
      console.error("Failed to persist drag-sort order:", error);
      this.broadcastSave("error");
    }
  }

  private broadcastSave(state: "saving" | "saved" | "error") {
    window.dispatchEvent(
      new CustomEvent("survey-save", { detail: { state } }),
    );
  }
}
